import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AFFILIATE_PAGE_SOURCE,
  trackPricedAffiliateDealClick,
  trackPrimaryCtaClick,
} from '../utils/enhancedAnalytics';
import enhancedAnalytics from '../utils/enhancedAnalytics';
import {
  HOME_COMMERCE_PRODUCTS,
  loadHomeCommerceItem,
} from './homeCommerceOffers';
import EarlyAccessCallout from './earlyAccess/EarlyAccessCallout';
import {
  HOME_DOCUMENT_TITLE,
  HOME_META_DESCRIPTION,
  HOME_PATH,
} from '../seo/pageSeo';
import { usePageSeo } from '../seo/usePageSeo';
import './LandingPage.css';
import { HomeIceFishingSection } from './gear/IceFishingPage';
import HomeDealsPreview from './HomeDealsPreview';
import SiteSearch from './search/SiteSearch';

function LandingPage() {
  usePageSeo({
    title: HOME_DOCUMENT_TITLE,
    description: HOME_META_DESCRIPTION,
    path: HOME_PATH,
  });
  const navigate = useNavigate();
  const [commerceItems, setCommerceItems] = useState(
    HOME_COMMERCE_PRODUCTS.map((item) => ({
      ...item,
      name: item.label,
      imageSrc: item.fallbackImage,
      offer: null,
      priceLabel: null,
      retailer: null,
      freshness: null,
      affiliateUrl: '',
      salePrice: null,
      hasCurrent: false,
      isTie: false,
      tiedCount: 0,
      kicker: null,
      tieNote: null,
      offersResolved: false,
    }))
  );

  useEffect(() => {
    let cancelled = false;
    Promise.all(HOME_COMMERCE_PRODUCTS.map(loadHomeCommerceItem)).then((items) => {
      if (!cancelled) setCommerceItems(items);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCustomBuilderClick = (event) => {
    event.preventDefault();
    enhancedAnalytics.trackEvent('bundle_builder_start', {
      source: 'landing_page',
      button_location: 'more_tools',
    });
    navigate('/custom-builder');
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  return (
    <div className="ab-home">
      <section className="ab-home-hero" aria-labelledby="home-hero-heading">
        <div className="ab-home-hero-inner">
          <div className="ab-home-hero-copy">
            <p className="ab-home-eyebrow">12 RETAILERS. ONE PLACE TO COMPARE.</p>
            <h1 id="home-hero-heading" className="ab-home-title">Find the best price.<br/><span>Gear up for less.</span></h1>
            <p className="ab-home-lede">
              We check 12 retailers to help you save on fishing gear. Compare current
              prices, spot verified deals, and find the right setup for ice or open water.
            </p>
            <SiteSearch hero/>
            <div className="ab-home-hero-actions">
              <Link
                className="ab-home-cta ab-home-cta-primary"
                to="/price-comparison"
                data-testid="home-primary-cta"
                onClick={() => {
                  trackPrimaryCtaClick({
                    pageSource: AFFILIATE_PAGE_SOURCE.HOMEPAGE,
                    placement: 'homepage_hero',
                    cta: 'find_best_price',
                  });
                }}
              >
                Find the best price <span aria-hidden="true">→</span>
              </Link>
            </div>
          <nav className="ab-home-quick-links" aria-label="Explore Angler Built">
            {[
              {to:'/ice-fishing/new-gear-2026-27',title:'Discover new gear',description:'This season’s releases and brand guides.',cta:'discover_new_gear'},
              {to:'/compare',title:'Compare electronics',description:'See the differences. Choose what fits.',cta:'compare_electronics'},
              {to:'/compatibility',title:'Check compatibility',description:'Find what works with your Garmin setup.',cta:'check_compatibility'},
            ].map(item=><Link key={item.to} title={item.description} to={item.to} onClick={()=>trackPrimaryCtaClick({pageSource:AFFILIATE_PAGE_SOURCE.HOMEPAGE,placement:'homepage_hero',cta:item.cta})}>
              {item.title} <span aria-hidden="true">↗</span>
            </Link>)}
          </nav>
          </div>
          <HomeDealsPreview/>
        </div>
      </section>

      <HomeIceFishingSection />

      <section
        className="ab-home-decision"
        aria-labelledby="home-problems-heading"
      >
        <div className="ab-home-inner">
          <h2 id="home-problems-heading" className="ab-home-section-title">
            Get more from your electronics.
          </h2>
          <div className="ab-home-paths">
            <article className="ab-home-path">
              <p className="ab-home-path-index">01 Compare LiveScope</p>
              <h3 className="ab-home-path-question">Which one should I choose?</h3>
              <p className="ab-home-path-copy">
                See how LVS42HD and LVS44 differ, and which LiveScope 2 path
                fits how you fish.
              </p>
              <Link
                className="ab-home-path-link"
                to="/comparisons/lvs42hd-vs-lvs44"
              >
                Compare LVS42HD vs LVS44
                <span aria-hidden="true"> →</span>
              </Link>
            </article>
            <article className="ab-home-path">
              <p className="ab-home-path-index">02 Check compatibility</p>
              <h3 className="ab-home-path-question">
                Will it work with my Garmin display?
              </h3>
              <p className="ab-home-path-copy">
                Check supported Garmin display + LiveScope combinations and what
                the setup requires.
              </p>
              <Link className="ab-home-path-link" to="/compatibility">
                Check Compatibility
                <span aria-hidden="true"> →</span>
              </Link>
            </article>
            <article className="ab-home-path">
              <p className="ab-home-path-index">03 Current prices</p>
              <h3 className="ab-home-path-question">Where should I buy it?</h3>
              <p className="ab-home-path-copy">
                See current retailer pricing and availability for the gear you
                already decided on.
              </p>
              <Link className="ab-home-path-link" to="/price-comparison">
                See Current Prices
                <span aria-hidden="true"> →</span>
              </Link>
            </article>
          </div>
          <EarlyAccessCallout page="homepage" placement="homepage" />
        </div>
      </section>

      <section className="ab-home-tools" aria-labelledby="home-tools-heading">
        <div className="ab-home-inner">
          <h2 id="home-tools-heading" className="ab-home-section-title">
            More Angler Built Tools
          </h2>
          <div className="ab-home-tools-grid">
            <Link
              to="/custom-builder"
              className="ab-home-tool"
              onClick={handleCustomBuilderClick}
            >
              <div className="ab-home-tool-media">
                <img
                  src="/images/GPS-GEMU295LSB_0.webp"
                  alt="Garmin display and LiveScope parts used in the Custom Bundle Builder"
                />
              </div>
              <div className="ab-home-tool-body">
                <h3>Custom Bundle Builder</h3>
                <p>Build a boat or ice parts list from the existing setup tool.</p>
                <span>Open builder →</span>
              </div>
            </Link>
            <Link to="/compare-screens" className="ab-home-tool">
              <div className="ab-home-tool-media">
                <img
                  src="/images/68798-Chartplotter-Blog-header-image.webp"
                  alt="Row of Garmin displays used in the Screen Finder"
                />
              </div>
              <div className="ab-home-tool-body">
                <h3>Garmin Screen Finder</h3>
                <p>Filter and compare Garmin displays by size and features.</p>
                <span>Find a display →</span>
              </div>
            </Link>
            <Link to="/select/battery" className="ab-home-tool">
              <div className="ab-home-tool-media">
                <img
                  src="/images/batteries/amped-30ah-battery.webp"
                  alt="Lithium battery used in the Battery Finder"
                />
              </div>
              <div className="ab-home-tool-body">
                <h3>Battery Finder</h3>
                <p>Pick a battery size and chemistry for your electronics.</p>
                <span>Find a battery →</span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <section
        className="ab-home-commerce"
        aria-labelledby="home-commerce-heading"
      >
        <div className="ab-home-inner">
          <h2 id="home-commerce-heading" className="ab-home-section-title">
            Already know what you want?
          </h2>
          <p className="ab-home-commerce-lede">
            Current LiveScope 2 retailer offers, if you are ready to buy.
          </p>
          <div className="ab-home-commerce-grid">
            {commerceItems.map((item) => (
              <article
                key={item.slug}
                className="ab-home-commerce-item"
                data-testid={`home-commerce-${item.slug}`}
              >
                <div className="ab-home-commerce-visual">
                  <img src={item.imageSrc} alt={item.name} />
                </div>
                <div className="ab-home-commerce-body">
                  <h3>{item.name}</h3>
                  {item.hasCurrent && item.offer && item.affiliateUrl ? (
                    <>
                      <p
                        className="ab-home-commerce-kicker"
                        data-testid={`home-commerce-kicker-${item.slug}`}
                      >
                        {item.kicker}
                      </p>
                      <p className="ab-home-commerce-price">{item.priceLabel}</p>
                      <p className="ab-home-commerce-meta">
                        {item.isTie ? item.tieNote : item.retailer}
                        {item.freshness ? ` · ${item.freshness}` : ''}
                      </p>
                      <div className="ab-home-commerce-actions">
                        <a
                          className="ab-home-commerce-buy"
                          href={item.affiliateUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => {
                            trackPricedAffiliateDealClick({
                              retailer: item.retailer,
                              affiliateUrl: item.affiliateUrl,
                              salePrice: item.salePrice,
                              pageSource: AFFILIATE_PAGE_SOURCE.HOMEPAGE,
                              productSlug: item.slug,
                              placement: 'homepage_commerce',
                            });
                          }}
                        >
                          Buy at {item.retailer}
                        </a>
                        <Link className="ab-home-commerce-more" to={item.pdpPath}>
                          Compare all prices
                        </Link>
                      </div>
                    </>
                  ) : item.offersResolved ? (
                    <>
                      <p
                        className="ab-home-commerce-kicker"
                        data-testid={`home-commerce-delayed-${item.slug}`}
                      >
                        Current price verification delayed
                      </p>
                      <Link className="ab-home-commerce-buy" to={item.pdpPath}>
                        See retailer offers
                      </Link>
                    </>
                  ) : (
                    <Link className="ab-home-commerce-buy" to={item.pdpPath}>
                      See retailer offers
                    </Link>
                  )}
                </div>
              </article>
            ))}
          </div>
          {commerceItems.some((item) => item.hasCurrent && item.offer && item.affiliateUrl) ? (
            <p className="ab-home-disclosure">
              Angler Built may earn a commission when you purchase through
              retailer links, at no additional cost to you.
            </p>
          ) : null}
        </div>
      </section>
    </div>
  );
}

export default LandingPage;
